import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ReactNode } from 'react';

import { useI18nContext } from '@/i18n/I18n';
import { IPermission } from '@/services/types/apiManagement';
export interface IPermissionRecord {
  id: number;
  type: string;
  value: string;
  businessType: ReactNode;
  permissionName: ReactNode;
  writePermission: ReactNode;
  readPermission: string;
  hide?: boolean;
  disabled?: boolean;
}
// 分别对应子/母账号的值
/**
 * 大写字母开头的母账号权限，小写字母开头的是子账号权限项
 */
export const permissionMap: any = {
  1: [{ ContractTrade: 'Order' }, { contractTrade: 'API_PERMISSION_ORDER' }],
  2: [
    { ContractTrade: 'Position' },
    { contractTrade: 'API_PERMISSION_POSITION' },
  ],
  4: [{ Options: 'OptionsTrade' }, { options: 'API_PERMISSION_OPTIONS_TRADE' }],
  5: [{ Spot: 'SpotTrade' }, { spot: 'API_PERMISSION_SPOT_TRADE' }],
  6: [{ CopyTrading: 'CopyTrading' }],
  7: [
    { Wallet: 'AccountTransfer' },
    { wallet: 'API_PERMISSION_ACCOUNT_TRANSFER' },
  ],
  // 子账号没有母子账号划转权限，因此屏蔽
  8: [
    { Wallet: 'SubMemberTransfer' },
    { wallet: 'API_PERMISSION_SUBMEMBER_TRANSFER' },
  ],
  9: [{ Wallet: 'Withdraw' }],
  10: [
    { Derivatives: 'DerivativesTrade' },
    { derivatives: 'API_PERMISSION_DERIVATIVES_TRADE' },
  ],
  11: [
    { Exchange: 'ExchangeHistory' },
    { exchange: 'API_PERMISSION_EXCHANGE_HISTORY' },
  ],
  12: [
    { BlockTrade: 'BlockTrade' },
    { blockTrade: 'API_PERMISSION_BLOCK_TRADE' },
  ],
  13: [{ NFT: 'NFTQueryProductList' }],
  14: [
    { Wallet: 'SubMemberTransferList' },
    { wallet: 'API_PERMISSION_SUBMEMBER_TRANSFER_LIST' },
  ],
};
// 查询某一type的index以及value
const getRealPermissionKey = (
  isSubAccount: boolean,
  type: string,
  permission: string[],
) => {
  const index = isSubAccount ? 1 : 0;
  const realKey: number[] = [];
  Object.keys(permissionMap).forEach((key: string) => {
    const currentPermission = permissionMap[key] || [];
    const realPermission = currentPermission[index] || {};
    if (
      currentPermission?.length >= index + 1 &&
      permission.includes(realPermission[type])
    ) {
      realKey.push(Number(key));
    }
  });
  return realKey;
};
export const permissionMapKey = (
  permission: IPermission = {} as IPermission,
  isSubAccount: boolean,
) => {
  const permissionKey: number[] = [];
  Object.entries(permission).forEach(
    ([type, permission]: [key: string, permission: string[]]) => {
      permissionKey.push(
        ...getRealPermissionKey(isSubAccount, type, permission || []),
      );
    },
  );
  return permissionKey;
};

export const calPermission = (keys: React.Key[]) => {
  const permissions: any = {};
  const index = 0;

  keys.forEach((attr: React.Key) => {
    const keyPermission = permissionMap[attr][index] || {};
    if (Object.keys(keyPermission)) {
      const [key, value] = Object.entries(keyPermission)[0];
      if (!permissions[key]) {
        permissions[key] = [];
      }
      permissions[key]?.push(value);
    }
  });
  return permissions;
};

export const mergeCells = (dataSource: any[], key = 'type') => {
  const rowSpanArr = [];
  //保存上一个key
  let x = '';
  // 相同key出现的次数
  let count = 0;
  // 该key第一次出现的位置
  let startIndex = 0;

  for (let i = 0; i < dataSource.length; i++) {
    //合并key列
    const val = dataSource[i][key];
    if (i == 0) {
      x = val;
      count = 1;
      rowSpanArr[0] = 1;
    } else {
      if (val == x) {
        ++count;
        rowSpanArr[startIndex] = count;
        rowSpanArr[i] = 0;
      } else {
        count = 1;
        x = val;
        startIndex = i;
        rowSpanArr[i] = 1;
      }
    }
  }
  return rowSpanArr;
};
export const usePermission = () => {
  const [$t] = useI18nContext();
  const getPermissionColumns = (
    dataSource: IPermissionRecord[],
    readOnly: boolean,
  ) => {
    const columns: ColumnsType<IPermissionRecord> = [
      {
        title: $t('businessType'),
        dataIndex: 'businessType',
        width: 120,
        onCell: (_, index) => {
          const rowSpanArr: any = mergeCells(dataSource);
          return { rowSpan: rowSpanArr[index as number] };
        },
      },
      Table.SELECTION_COLUMN,
      {
        title: $t('jurisdictionName'),
        dataIndex: 'permissionName',
        width: 180,
      },
      {
        title: $t('readWrightP0'),
        dataIndex: 'writePermission',
      },
      {
        title: $t('onlyReadP0'),
        dataIndex: 'readPermission',
      },
    ];
    if (readOnly) {
      columns.splice(3, 1);
    } else {
      columns.pop();
    }
    return columns;
  };
  const getPermissionDataSource = (hasKyc: boolean) => {
    const dataSource = [
      {
        id: 1,
        type: 'ContractTrade',
        value: 'Order',
        businessType: $t('Contract'),
        permissionName: $t('perOrder'),
        writePermission: $t('readWrightP2'),
        readPermission: $t('onlyReadP2'),
      },
      {
        id: 2,
        type: 'ContractTrade',
        value: 'Position',
        businessType: $t('Contract'),
        permissionName: $t('perPosition'),
        writePermission: $t('readWrightP1'),
        readPermission: $t('onlyReadP1'),
      },
      {
        id: 4,
        type: 'Options',
        value: 'OptionsTrade',
        businessType: (
          <Tooltip title={$t('USDCContractTip')}>{$t('USDCContract')}</Tooltip>
        ),
        permissionName: $t('USDCContractTrade'),
        writePermission: $t('readWrightP8'),
        readPermission: $t('onlyReadP8'),
        hide: false,
      },
      {
        id: 10,
        type: 'Derivatives',
        value: 'DerivativesTrade',
        businessType: $t('Derivatives API V3'),
        permissionName: $t('Trade'),
        writePermission: $t('derivatives read-write access'),
        readPermission: $t('derivatives read-only access'),
        hide: false,
      },
      {
        id: 5,
        type: 'Spot',
        value: 'SpotTrade',
        businessType: $t('spot'),
        permissionName: $t('Trade'),
        writePermission: $t('readWrightP3'),
        readPermission: $t('onlyReadP3'),
      },
      {
        id: 12,
        type: 'BlockTrade',
        value: 'BlockTrade',
        businessType: $t('perBlockTrade'),
        permissionName: (
          <Space size={4}>
            <span>{$t('perBlockTrade')}</span>
            {!hasKyc && (
              <Tooltip title={$t('KYCTip')}>
                <QuestionCircleOutlined />
              </Tooltip>
            )}
          </Space>
        ),
        writePermission: $t('soptBlockTrade'),
        readPermission: $t('spotBlockTradeReadOnly'),
        hide: false,
      },
      // {
      //   id: 6,
      //   type: 'CopyTrading',
      //   value: 'CopyTrading',
      //   businessType: $t('CopyTrading'),
      //   permissionName: $t('Trade'),
      //   writePermission: $t('copyTradingWriteRead'),
      //   readPermission: $t('copyTradingReadOnly'),
      //   hide: false,
      // },
      {
        id: 7,
        type: 'Wallet',
        value: 'AccountTransfer',
        businessType: $t('Wallet'),
        permissionName: $t('accountTransfer'),
        writePermission: $t('readWrightP5'),
        readPermission: $t('onlyReadP5'),
      },
      {
        id: 8,
        type: 'Wallet',
        value: 'SubMemberTransfer',
        businessType: $t('Wallet'),
        permissionName: $t('subaccountTransfer'),
        writePermission: $t('readWrightP9'),
        readPermission: $t('onlyReadP9'),
      },
      // {
      //   id: 11,
      //   type: 'Exchange',
      //   value: 'ExchangeHistory',
      //   businessType: $t('assetsExchange.exchange'),
      //   permissionName: $t('exchangeHistoryCheck'),
      //   writePermission: $t('exchangeHistoryCheckRW'),
      //   readPermission: $t('exchangeHistoryCheckRO'),
      //   hide: false,
      // },
      // {
      //   id: 13,
      //   type: 'NFT',
      //   value: 'NFTQueryProductList',
      //   businessType: 'NFT',
      //   permissionName: $t('perNFT'),
      //   writePermission: $t('nftReadWrite'),
      //   readPermission: $t('nftReadOnly'),
      //   hide: false,
      // },
    ];
    return dataSource.filter((data) => !data.hide);
  };
  return { getPermissionColumns, getPermissionDataSource };
};
